(() => {
  if (window.TONOMAX_VIDEO_PLAYBACK) {
    window.TONOMAX_VIDEO_PLAYBACK.init();
    return;
  }

  const states = new Map();
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const isVisible = (video) => {
    const rect = video.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0
      && rect.top < window.innerHeight && rect.left < window.innerWidth;
  };

  function attemptPlayback(video, userGesture = false) {
    const state = states.get(video);
    if (!state || !video.isConnected || document.hidden || !isVisible(video)) return;
    if (!userGesture && (reducedMotion.matches || state.userPaused || state.blocked)) return;
    if (state.pending || !video.paused) return;
    if (video.error) {
      if (!userGesture) return;
      video.load();
    }

    // Keep this call synchronous inside click/touch handlers. Webviews can
    // require user activation even for a muted, inline video.
    state.pending = true;
    try {
      const playback = video.play();
      if (playback && typeof playback.then === "function") {
        playback.then(() => {
          state.pending = false;
          state.blocked = false;
        }, (error) => {
          state.pending = false;
          state.blocked = !error || error.name !== "AbortError";
          video.controls = false;
        });
      } else {
        state.pending = false;
      }
    } catch (error) {
      state.pending = false;
      state.blocked = true;
      video.controls = false;
    }
  }

  const observer = "IntersectionObserver" in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        const state = states.get(video);
        if (!state) return;
        state.visible = entry.isIntersecting && entry.intersectionRatio > 0;
        if (state.visible) attemptPlayback(video);
        else if (!video.paused) video.pause();
      });
    }, { threshold: [0, 0.15] })
    : null;

  function init() {
    document.querySelectorAll("video[data-tonomax-video]").forEach((video) => {
      if (states.has(video)) return;
      const state = { pending: false, blocked: false, userPaused: false, started: false, ignoreNextPause: false, visible: isVisible(video) };
      states.set(video, state);
      video.defaultMuted = true;
      video.muted = true;
      video.playsInline = true;
      video.controls = false;
      video.removeAttribute("controls");
      video.autoplay = !reducedMotion.matches;
      video.setAttribute("muted", "");
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "");
      video.addEventListener("loadeddata", () => attemptPlayback(video));
      video.addEventListener("canplay", () => attemptPlayback(video));
      video.addEventListener("playing", () => {
        state.started = true;
        state.blocked = false;
        state.userPaused = false;
      });
      video.addEventListener("pause", () => {
        if (state.ignoreNextPause) {
          state.ignoreNextPause = false;
          return;
        }
        if (state.started && state.visible && isVisible(video) && !document.hidden) state.userPaused = true;
      });
      if (reducedMotion.matches && !video.paused) {
        state.ignoreNextPause = true;
        video.pause();
      }
      if (observer) observer.observe(video);
      attemptPlayback(video);
    });
    states.forEach((state, video) => {
      if (!video.isConnected) {
        if (observer) observer.unobserve(video);
        states.delete(video);
      }
    });
  }

  function resumeVisible(resetBlocked = false) {
    states.forEach((state, video) => {
      if (resetBlocked) state.blocked = false;
      state.visible = isVisible(video);
      if (state.visible) attemptPlayback(video);
      else if (!video.paused) video.pause();
    });
  }

  function onUserGesture(event) {
    if (event.type === "keydown" && event.key !== "Enter" && event.key !== " ") return;
    const tappedVideo = event.target && event.target.closest
      ? event.target.closest("video[data-tonomax-video]")
      : null;
    if (tappedVideo && states.has(tappedVideo)) {
      if (event.type === "keydown" && event.preventDefault) event.preventDefault();
      states.get(tappedVideo).userPaused = false;
      attemptPlayback(tappedVideo, true);
      return;
    }
    if (reducedMotion.matches) return;
    states.forEach((state, video) => {
      if (!state.userPaused) attemptPlayback(video, true);
    });
  }

  window.TONOMAX_VIDEO_PLAYBACK = { init };
  document.addEventListener("touchend", onUserGesture, { passive: true });
  document.addEventListener("click", onUserGesture);
  document.addEventListener("keydown", onUserGesture);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) states.forEach((state, video) => { if (!video.paused) video.pause(); });
    else resumeVisible(true);
  });
  document.addEventListener("shopify:section:load", init);
  window.addEventListener("pageshow", () => { init(); resumeVisible(true); });
  if (!observer) window.addEventListener("scroll", () => resumeVisible(), { passive: true });
  const onMotionChange = () => {
    states.forEach((state, video) => {
      video.autoplay = !reducedMotion.matches;
      if (reducedMotion.matches && !video.paused) {
        state.ignoreNextPause = true;
        video.pause();
      }
    });
    if (!reducedMotion.matches) resumeVisible(true);
  };
  if (reducedMotion.addEventListener) reducedMotion.addEventListener("change", onMotionChange);
  else reducedMotion.addListener(onMotionChange);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true });
  else init();
})();
